import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, Search, Eye, Star, Code, Globe, Zap, TrendingUp, Bot } from 'lucide-react';

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function Reveal({ children, delay = 0 }) {
  const [ref, inView] = useInView(0.08);
  return (
    <div ref={ref} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? 'none' : 'translateY(24px)',
      transition: `opacity 0.6s ease ${delay}s, transform 0.6s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
    }}>
      {children}
    </div>
  );
}

const categoryConfig = {
  'Gesundheit':           { color: '#10b981', Icon: TrendingUp },
  'Homelab':              { color: '#06b6d4', Icon: Code },
  'Website-Strategie':    { color: '#6366f1', Icon: Globe },
  'Künstliche Intelligenz': { color: '#a855f7', Icon: Bot },
  'Mobilität':            { color: '#f59e0b', Icon: Zap },
};

export default function DerrDigitalBlog({ darkMode = false }) {
  const [selectedCategory, setSelectedCategory] = useState('alle');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const navigate = useNavigate();

  const bg          = darkMode ? '#0a0a0f' : '#f8faff';
  const textPrimary = darkMode ? '#ffffff' : '#0f0f1a';
  const textSecondary = darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(15,15,26,0.55)';
  const cardBg      = darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.9)';
  const cardBorder  = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const inputBg     = darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)';
  const accent      = '#6366f1';
  const accent2     = '#8b5cf6';

  useEffect(() => {
    document.documentElement.style.background = bg;
    document.body.style.background = bg;
    return () => { document.documentElement.style.background = ''; document.body.style.background = ''; };
  }, [bg]);

  const blogPosts = [
    { id: 1, title: 'Bandscheibenvorfall vorbeugen: Gesund durch den 9-Stunden-Büroalltag', excerpt: 'Effektive Strategien und praktische Übungen, um Rückenproblemen trotz langem Sitzen im Büro vorzubeugen.', author: 'Nele Schumacher', date: '2025-06-03', readTime: '10 min', category: 'Gesundheit', tags: ['Rückengesundheit', 'Büroarbeit', 'Ergonomie'], views: 2847, likes: 156 },
    { id: 2, title: 'PiHole auf Proxmox installieren: Schritt-für-Schritt Anleitung', excerpt: 'Wie Sie PiHole als Ubuntu Container auf Proxmox einrichten und Ihr Netzwerk vor Werbung und Trackern schützen.', author: 'Dustin Derr', date: '2025-06-04', readTime: '12 min', category: 'Homelab', tags: ['PiHole', 'Proxmox', 'DNS'], views: 2841, likes: 167 },
    { id: 3, title: 'Warum eine eigene Unternehmenswebsite heute unverzichtbar ist', excerpt: 'Warum eine professionelle Website der Schlüssel zum Geschäftserfolg ist und wie Sie Ihre Umsätze nachhaltig steigern.', author: 'Nele Schumacher', date: '2025-06-03', readTime: '9 min', category: 'Website-Strategie', tags: ['Website', 'Marketing', 'ROI'], views: 1542, likes: 127 },
    { id: 4, title: 'Künstliche Intelligenz: Revolution von heute bis morgen', excerpt: 'Wie KI bereits heute unser Leben prägt und welche bahnbrechenden Anwendungen die Zukunft bereithalten.', author: 'Nele Schumacher', date: '2025-06-03', readTime: '12 min', category: 'Künstliche Intelligenz', tags: ['KI', 'Machine Learning', 'Innovation'], views: 2847, likes: 156 },
    { id: 5, title: 'Die Revolution der Mobilität: Welche Antriebe werden die Zukunft bestimmen?', excerpt: 'Eine philosophische Reise durch die Antriebstechnologien von morgen — von Elektromobilität bis zu Wasserstoff.', author: 'Nele Schumacher', date: '2025-06-03', readTime: '12 min', category: 'Mobilität', tags: ['Elektromobilität', 'Wasserstoff', 'Nachhaltigkeit'], views: 2847, likes: 156 },
  ];

  const categories = [
    { key: 'alle', label: 'Alle' },
    ...Object.keys(categoryConfig).map(k => ({ key: k, label: k })),
  ];

  const popularTags = ['JavaScript', 'React', 'SEO', 'Performance', 'UI/UX', 'API', 'Security', 'Cloud', 'Mobile', 'CMS'];

  const filteredPosts = blogPosts.filter(post => {
    const matchesCat = selectedCategory === 'alle' || post.category === selectedCategory;
    const q = searchTerm.toLowerCase();
    const matchesSearch = !q || post.title.toLowerCase().includes(q) || post.excerpt.toLowerCase().includes(q) || post.tags.some(t => t.toLowerCase().includes(q));
    return matchesCat && matchesSearch;
  });

  const latestPosts = [...blogPosts].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 4);

  const getCatColor = (cat) => categoryConfig[cat]?.color || accent;
  const getCatIcon = (cat) => categoryConfig[cat]?.Icon || Globe;

  const initials = (name) => name.split(' ').map(n => n[0]).join('');
  const fmtDate  = (d) => new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <div style={{ background: bg, color: textPrimary, minHeight: '100vh' }}>

      {/* ── HERO ── */}
      <section style={{ position: 'relative', paddingTop: 'clamp(100px, 14vh, 140px)', paddingBottom: 56, overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: darkMode
          ? 'radial-gradient(ellipse 70% 50% at 50% 0%, rgba(99,102,241,0.14) 0%, transparent 65%)'
          : 'radial-gradient(ellipse 70% 50% at 50% 0%, rgba(99,102,241,0.07) 0%, transparent 65%)' }} />

        <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 24px', textAlign: 'center', position: 'relative' }}>
          <Reveal>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', color: accent2, textTransform: 'uppercase', marginBottom: 14 }}>Blog</div>
            <h1 style={{ fontSize: 'clamp(28px, 4.5vw, 60px)', fontWeight: 900, lineHeight: 1.05, letterSpacing: '-0.03em', marginBottom: 14 }}>
              <span style={{ background: `linear-gradient(135deg, ${accent}, ${accent2}, #a855f7)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                Digital Insights
              </span>{' '}& Expertise
            </h1>
            <p style={{ fontSize: 'clamp(13px, 1.4vw, 16px)', color: textSecondary, lineHeight: 1.65, marginBottom: 28, maxWidth: 500, margin: '0 auto 28px' }}>
              Expertenbeiträge zu Webentwicklung, KI, Homelab und digitalen Trends.
            </p>

            {/* Search */}
            <div style={{ position: 'relative', maxWidth: 480, margin: '0 auto' }}>
              <Search size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: textSecondary, pointerEvents: 'none' }} />
              <input
                type="text"
                placeholder="Artikel durchsuchen…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                style={{
                  width: '100%', paddingLeft: 40, paddingRight: 16, paddingTop: 11, paddingBottom: 11,
                  borderRadius: 12, fontSize: 14, background: inputBg, color: textPrimary, outline: 'none',
                  border: `1px solid ${searchFocused ? accent : cardBorder}`,
                  boxShadow: searchFocused ? `0 0 0 3px ${accent}20` : 'none',
                  transition: 'border-color 0.2s, box-shadow 0.2s', boxSizing: 'border-box',
                }}
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── CATEGORY PILLS (mobile + desktop) ── */}
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px 24px' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {categories.map(c => {
            const active = selectedCategory === c.key;
            const color = c.key === 'alle' ? accent : getCatColor(c.key);
            return (
              <button key={c.key} onClick={() => setSelectedCategory(c.key)} style={{
                padding: '6px 14px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
                background: active ? color : 'transparent',
                color: active ? '#fff' : textSecondary,
                outline: `1px solid ${active ? color : cardBorder}`,
                transition: 'all 0.2s',
              }}
                onMouseEnter={e => { if (!active) { e.currentTarget.style.outlineColor = color; e.currentTarget.style.color = color; } }}
                onMouseLeave={e => { if (!active) { e.currentTarget.style.outlineColor = cardBorder; e.currentTarget.style.color = textSecondary; } }}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── MAIN LAYOUT ── */}
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px 80px', display: 'grid', gridTemplateColumns: '1fr', gap: 32 }} className="blog-layout">
        <style>{`@media(min-width:1024px){.blog-layout{grid-template-columns:1fr 260px!important}}.blog-sidebar{display:none}@media(min-width:1024px){.blog-sidebar{display:flex!important}}`}</style>

        {/* Articles grid */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <span style={{ fontSize: 13, color: textSecondary }}>{filteredPosts.length} Artikel</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
            {filteredPosts.map((post, i) => {
              const color = getCatColor(post.category);
              const CatIcon = getCatIcon(post.category);
              return (
                <Reveal key={post.id} delay={i * 0.06}>
                  <article
                    onClick={() => navigate(`/blog/${post.id}`)}
                    style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 16, overflow: 'hidden', cursor: 'pointer', transition: 'border-color 0.25s, transform 0.25s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = color + '45'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = cardBorder; e.currentTarget.style.transform = 'none'; }}
                  >
                    {/* Card image area */}
                    <div style={{ height: 100, background: `linear-gradient(135deg, ${color}22, ${color}08)`, borderBottom: `1px solid ${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${color}, transparent)` }} />
                      <CatIcon size={32} color={color + 'aa'} strokeWidth={1.3} />
                    </div>

                    <div style={{ padding: '16px 18px' }}>
                      {/* Meta row */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color, background: color + '15', padding: '2px 8px', borderRadius: 999 }}>{post.category}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: textSecondary }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11 }}><Eye size={11} />{post.views}</span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11 }}><Star size={11} />{post.likes}</span>
                        </div>
                      </div>

                      <h2 style={{ fontSize: 15, fontWeight: 800, lineHeight: 1.35, color: textPrimary, marginBottom: 8, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {post.title}
                      </h2>
                      <p style={{ fontSize: 13, color: textSecondary, lineHeight: 1.6, marginBottom: 12, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {post.excerpt}
                      </p>

                      {/* Tags */}
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'nowrap', overflow: 'hidden', marginBottom: 14 }}>
                        {post.tags.slice(0, 3).map(tag => (
                          <span key={tag} style={{ fontSize: 11, color: textSecondary, background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', padding: '2px 7px', borderRadius: 5 }}>#{tag}</span>
                        ))}
                      </div>

                      {/* Footer */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: `1px solid ${cardBorder}`, paddingTop: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 28, height: 28, borderRadius: '50%', background: `linear-gradient(135deg, ${color}, ${color}88)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                            {initials(post.author)}
                          </div>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: textPrimary }}>{post.author}</div>
                            <div style={{ fontSize: 11, color: textSecondary, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <Calendar size={9} />{fmtDate(post.date)}
                              <span style={{ opacity: 0.4 }}>·</span>
                              <Clock size={9} />{post.readTime}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color }}>
                          Lesen <ArrowRight size={12} />
                        </div>
                      </div>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>

          {filteredPosts.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 0', color: textSecondary }}>
              <Search size={32} style={{ margin: '0 auto 12px', opacity: 0.4, display: 'block' }} />
              <div style={{ fontSize: 15, fontWeight: 600 }}>Keine Artikel gefunden</div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="blog-sidebar" style={{ flexDirection: 'column', gap: 16, alignSelf: 'start', position: 'sticky', top: 80 }}>

          {/* Categories */}
          <div style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 14, padding: '18px 16px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: textSecondary, marginBottom: 12 }}>Kategorien</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {categories.map(c => {
                const active = selectedCategory === c.key;
                const color = c.key === 'alle' ? accent : getCatColor(c.key);
                return (
                  <button key={c.key} onClick={() => setSelectedCategory(c.key)} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 10px', borderRadius: 8, border: 'none', cursor: 'pointer', textAlign: 'left',
                    background: active ? color + '18' : 'transparent',
                    transition: 'background 0.2s',
                  }}
                    onMouseEnter={e => { if (!active) e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'; }}
                    onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{ fontSize: 13, fontWeight: active ? 700 : 500, color: active ? color : textSecondary }}>{c.label}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: active ? color : textSecondary, background: active ? color + '20' : (darkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'), padding: '1px 7px', borderRadius: 999 }}>
                      {c.key === 'alle' ? blogPosts.length : blogPosts.filter(p => p.category === c.key).length}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tags */}
          <div style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 14, padding: '18px 16px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: textSecondary, marginBottom: 12 }}>Tags</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {popularTags.map(tag => (
                <button key={tag} onClick={() => setSearchTerm(tag)} style={{
                  fontSize: 11, padding: '4px 9px', borderRadius: 6, border: `1px solid ${cardBorder}`, background: 'transparent',
                  color: textSecondary, cursor: 'pointer', transition: 'border-color 0.2s, color 0.2s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = accent + '60'; e.currentTarget.style.color = accent; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = cardBorder; e.currentTarget.style.color = textSecondary; }}
                >
                  #{tag}
                </button>
              ))}
            </div>
          </div>

          {/* Latest */}
          <div style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 14, padding: '18px 16px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: textSecondary, marginBottom: 12 }}>Neueste Artikel</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {latestPosts.map(post => {
                const color = getCatColor(post.category);
                return (
                  <div key={post.id} onClick={() => navigate(`/blog/${post.id}`)} style={{ display: 'flex', gap: 10, cursor: 'pointer', alignItems: 'flex-start' }}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: color + '18', border: `1px solid ${color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color }}>
                      {React.createElement(getCatIcon(post.category), { size: 15, strokeWidth: 1.8 })}
                    </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: textPrimary, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{post.title}</div>
                      <div style={{ fontSize: 11, color: textSecondary, marginTop: 3 }}>{fmtDate(post.date)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </aside>
      </div>

      {/* ── NEWSLETTER ── */}
      <section style={{ padding: '64px 0', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: darkMode ? 'rgba(99,102,241,0.04)' : 'rgba(99,102,241,0.03)', borderTop: `1px solid ${cardBorder}`, borderBottom: `1px solid ${cardBorder}` }} />
        <div style={{ maxWidth: 560, margin: '0 auto', padding: '0 24px', textAlign: 'center', position: 'relative' }}>
          <Reveal>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', color: accent2, textTransform: 'uppercase', marginBottom: 10 }}>Newsletter</div>
            <h2 style={{ fontSize: 'clamp(20px, 2.5vw, 32px)', fontWeight: 900, letterSpacing: '-0.02em', marginBottom: 10 }}>Kein Artikel verpassen</h2>
            <p style={{ fontSize: 14, color: textSecondary, lineHeight: 1.65, marginBottom: 24 }}>
              Neue Beiträge direkt in dein Postfach. Kostenlos, kein Spam, jederzeit abbestellbar.
            </p>
            <div style={{ display: 'flex', gap: 8, maxWidth: 420, margin: '0 auto' }}>
              <input type="email" placeholder="deine@email.de" style={{
                flex: 1, padding: '11px 14px', borderRadius: 10, fontSize: 14,
                background: inputBg, color: textPrimary, border: `1px solid ${cardBorder}`, outline: 'none', boxSizing: 'border-box',
              }}
                onFocus={e => e.target.style.borderColor = accent}
                onBlur={e => e.target.style.borderColor = cardBorder}
              />
              <button style={{
                padding: '11px 20px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13, color: '#fff', flexShrink: 0,
                background: `linear-gradient(135deg, ${accent}, ${accent2})`,
                boxShadow: `0 4px 14px ${accent}30`, transition: 'transform 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
              >
                Anmelden
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ padding: 'clamp(40px, 6vh, 60px) 0', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.25)' }} />
        <div style={{ maxWidth: 580, margin: '0 auto', padding: '0 24px', textAlign: 'center', position: 'relative' }}>
          <Reveal>
            <h2 style={{ fontSize: 'clamp(17px, 2vw, 26px)', fontWeight: 900, color: '#fff', lineHeight: 1.1, marginBottom: 10 }}>
              Fragen zu einem Artikel?
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, lineHeight: 1.65, marginBottom: 24 }}>
              Unser Team berät dich gerne — kostenlos und unverbindlich.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/kontakt" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 26px', borderRadius: 11,
                background: '#fff', color: accent, fontWeight: 700, fontSize: 13, textDecoration: 'none',
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)', transition: 'transform 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
              >
                Kostenlos beraten lassen <ArrowRight size={13} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
